import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import RichTextEditor from '@/components/RichTextEditor';
import { ChevronLeft, ChevronRight, Plus, Pencil, Trash2, CalendarDays, DollarSign, Bell, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { SearchableSelect } from '@/components/SearchableSelect';
import api from '@/lib/api';

const EVENT_TYPES = ['Vendor Payment Due', 'Recurring Payment', 'Rent Due Date', 'Payroll Deadline', 'AP Due Date'];
const RECURRENCE_PATTERNS = ['Daily', 'Weekly', 'Monthly', 'Annually', 'Custom'];
const REMINDER_OPTIONS = [
  { value: '1', label: '1 day before' }, { value: '3', label: '3 days before' },
  { value: '5,1', label: '5 days & 1 day before' },
  { value: '7', label: '1 week before' }, { value: '7,1', label: '1 week & 1 day before' },
  { value: '14', label: '2 weeks before' },
  { value: '30', label: '1 month before' },
];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const typeColors = {
  'Vendor Payment Due': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'Recurring Payment': 'bg-violet-500/20 text-violet-400 border-violet-500/30',
  'Rent Due Date': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  'Payroll Deadline': 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  'AP Due Date': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
};

const emptyForm = {
  name: '', event_type: '', date: '', time: '', all_day: true,
  recurring: false, recurrence_pattern: '', recurrence_days: [],
  recurrence_day_of_month: '', recurrence_interval: '',
  notes: '', linked_project: '', linked_section: '',
  reminder_days: '', create_je_draft: false, je_amount: '',
};

export default function AccountingCalendarView() {
  const { user } = useAuth();
  const canEdit = ['Administrator', 'Owner', 'Office Manager'].includes(user?.role);
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [selectedDay, setSelectedDay] = useState(null);
  const [projects, setProjects] = useState([]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const fetchEvents = useCallback(() => {
    const start = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const endMonth = month === 11 ? 1 : month + 2;
    const endYear = month === 11 ? year + 1 : year;
    const end = `${endYear}-${String(endMonth).padStart(2, '0')}-01`;
    api.get(`/accounting/calendar/events?start=${start}&end=${end}`).then(r => setEvents(r.data)).catch(() => {});
  }, [year, month]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);
  useEffect(() => { api.get('/projects').then(r => setProjects(r.data)).catch(() => {}); }, []);

  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  }, [year, month]);

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach(e => {
      const day = parseInt(e.date?.split('-')[2], 10);
      if (!map[day]) map[day] = [];
      map[day].push(e);
    });
    return map;
  }, [events]);

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => setCurrentDate(new Date());

  const openCreate = (day) => {
    if (!canEdit) return;
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setEditEvent(null);
    setForm({ ...emptyForm, date: dateStr });
    setSheetOpen(true);
  };

  const openEdit = (ev) => {
    if (!canEdit) return;
    setEditEvent(ev);
    setForm({
      name: ev.name || '', event_type: ev.event_type || '', date: ev.date || '',
      time: ev.time || '', all_day: ev.all_day !== false,
      recurring: ev.recurring || false, recurrence_pattern: ev.recurrence_pattern || '',
      recurrence_days: ev.recurrence_days || [], recurrence_day_of_month: ev.recurrence_day_of_month || '',
      recurrence_interval: ev.recurrence_interval || '',
      notes: ev.notes || '', linked_project: ev.linked_project || '',
      linked_section: ev.linked_section || '',
      reminder_days: Array.isArray(ev.reminder_days) ? ev.reminder_days.join(',') : (ev.reminder_days != null ? String(ev.reminder_days) : ''),
      create_je_draft: ev.create_je_draft || false,
      je_amount: ev.je_amount || '',
    });
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Event name is required');
    if (!form.event_type) return toast.error('Event type is required');
    if (!form.date) return toast.error('Date is required');

    const payload = {
      ...form,
      reminder_days: form.reminder_days ? form.reminder_days.split(',').map(d => parseInt(d.trim())).filter(d => !isNaN(d)) : null,
      je_amount: form.je_amount ? parseFloat(form.je_amount) : null,
    };
    try {
      if (editEvent) {
        await api.put(`/accounting/calendar/events/${editEvent.id}`, payload);
        toast.success('Event updated');
      } else {
        await api.post('/accounting/calendar/events', payload);
        toast.success('Event created');
        if (payload.create_je_draft && payload.je_amount && ['Vendor Payment Due', 'Recurring Payment'].includes(payload.event_type)) {
          toast.success('Journal entry draft created');
        }
      }
      setSheetOpen(false);
      fetchEvents();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async () => {
    if (!editEvent) return;
    try {
      await api.delete(`/accounting/calendar/events/${editEvent.id}`);
      toast.success('Event deleted');
      setSheetOpen(false);
      fetchEvents();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const today = new Date();
  const isToday = (day) => day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  const showJEDraftOption = form.event_type === 'Vendor Payment Due' || form.event_type === 'Recurring Payment';

  return (
    <div className="space-y-4" data-testid="accounting-calendar-view">
      {/* Month Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-8 w-8 border-border" onClick={prevMonth} data-testid="acal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="icon" className="h-8 w-8 border-border" onClick={nextMonth} data-testid="acal-next"><ChevronRight className="h-4 w-4" /></Button>
          <h2 className="font-heading font-bold text-lg ml-2">{monthName}</h2>
          <Button variant="ghost" size="sm" className="text-xs text-muted-foreground ml-2" onClick={goToday}>Today</Button>
        </div>
        {canEdit && (
          <Button size="sm" className="bg-primary text-primary-foreground text-xs gap-1" onClick={() => openCreate(today.getDate())} data-testid="acal-new-event">
            <Plus className="h-3 w-3" /> New Event
          </Button>
        )}
      </div>

      {/* Calendar Grid */}
      <div className="border border-border rounded-md overflow-hidden bg-card">
        <div className="grid grid-cols-7">
          {WEEKDAYS.map(d => (
            <div key={d} className="p-2 text-center text-xs uppercase tracking-wider text-muted-foreground border-b border-border font-medium bg-secondary/30">{d}</div>
          ))}
          {calendarDays.map((day, i) => {
            const dayEvents = day ? eventsByDate[day] || [] : [];
            const isSelected = selectedDay === day;
            return (
              <div
                key={i}
                className={`min-h-[80px] p-1 border-b border-r border-border cursor-pointer transition-colors
                  ${!day ? 'bg-secondary/10' : 'hover:bg-secondary/20'}
                  ${isToday(day) ? 'bg-primary/5' : ''}
                  ${isSelected ? 'bg-primary/10 ring-1 ring-primary/30' : ''}`}
                onClick={() => day && setSelectedDay(isSelected ? null : day)}
                onDoubleClick={() => day && canEdit && openCreate(day)}
              >
                {day && (
                  <>
                    <span className={`text-xs font-medium ${isToday(day) ? 'bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center' : ''}`}>
                      {day}
                    </span>
                    <div className="space-y-0.5 mt-0.5">
                      {dayEvents.slice(0, 3).map(ev => (
                        <div
                          key={ev.id}
                          className={`text-xs px-1 py-0.5 rounded truncate cursor-pointer ${typeColors[ev.event_type] || 'bg-muted text-foreground'}`}
                          onClick={e => { e.stopPropagation(); openEdit(ev); }}
                          data-testid={`acal-event-${ev.id}`}
                          title={ev.source === 'accounts_payable' && ev.ap_amount ? `${ev.name} · $${Number(ev.ap_amount).toLocaleString(undefined, {minimumFractionDigits: 2})}` : ev.name}
                        >
                          {ev.name}{ev.source === 'accounts_payable' && ev.ap_amount ? ` · $${Number(ev.ap_amount).toLocaleString()}` : ''}
                        </div>
                      ))}
                      {dayEvents.length > 3 && <span className="text-xs text-muted-foreground pl-1">+{dayEvents.length - 3} more</span>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Day Detail Panel */}
      {selectedDay && (
        <Card className="border-border shadow-none bg-card">
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium">{monthName.split(' ')[0]} {selectedDay}, {year}</p>
              {canEdit && (
                <Button size="sm" variant="outline" className="text-xs h-7 border-border gap-1" onClick={() => openCreate(selectedDay)}>
                  <Plus className="h-3 w-3" /> Add Event
                </Button>
              )}
            </div>
            {(eventsByDate[selectedDay] || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">No events on this day</p>
            ) : (
              <div className="space-y-1.5">
                {(eventsByDate[selectedDay] || []).map(ev => (
                  <div key={ev.id} className="flex items-center gap-2 p-2 rounded-md bg-background border border-border hover:border-primary/30 cursor-pointer transition-colors" onClick={() => openEdit(ev)}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{ev.name}</p>
                        <Badge variant="outline" className={`text-xs shrink-0 ${typeColors[ev.event_type] || ''}`}>{ev.event_type}</Badge>
                        {ev.source === 'accounts_payable' && ev.ap_amount > 0 && (
                          <span className="text-xs font-semibold text-orange-400">${Number(ev.ap_amount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                        {!ev.all_day && ev.time && <span>{ev.time}</span>}
                        {ev.project_name && <span>{ev.project_name}</span>}
                        {ev.recurring && <Badge variant="outline" className="text-xs">Recurring</Badge>}
                        {ev.create_je_draft && <Badge variant="outline" className="text-xs text-amber-400 border-amber-500/30"><DollarSign className="h-2.5 w-2.5 mr-0.5" />JE Draft</Badge>}
                        {ev.source === 'accounts_payable' && (
                          <span className="text-primary flex items-center gap-0.5"><ExternalLink className="h-2.5 w-2.5" /> View in Accounts Payable</span>
                        )}
                      </div>
                    </div>
                    {canEdit && <Pencil className="h-3 w-3 text-muted-foreground shrink-0" />}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Event Form Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-primary" />
              {editEvent ? 'Edit Financial Event' : 'New Financial Event'}
            </SheetTitle>
            <SheetDescription>Accounting calendar event</SheetDescription>
          </SheetHeader>
          <div className="space-y-3 mt-4">
            <div><Label className="text-xs text-muted-foreground">Event Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-background border-border" data-testid="acal-form-name" /></div>

            <div>
              <Label className="text-xs text-muted-foreground">Event Type *</Label>
              <Select value={form.event_type} onValueChange={v => {
                const updates = { event_type: v };
                if (v === 'Payroll Deadline' && !form.reminder_days) updates.reminder_days = '3';
                setForm({ ...form, ...updates });
              }}>
                <SelectTrigger className="bg-background border-border" data-testid="acal-form-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {EVENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-muted-foreground">Date *</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="bg-background border-border" data-testid="acal-form-date" /></div>
              <div><Label className="text-xs text-muted-foreground">Time</Label><Input type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} className="bg-background border-border" disabled={form.all_day} /></div>
            </div>

            <div className="flex items-center justify-between py-1">
              <Label className="text-xs text-muted-foreground">All Day</Label>
              <Switch checked={form.all_day} onCheckedChange={v => setForm({ ...form, all_day: v })} />
            </div>

            {/* Recurring */}
            <div className="flex items-center justify-between py-1">
              <Label className="text-xs text-muted-foreground">Recurring</Label>
              <Switch checked={form.recurring} onCheckedChange={v => setForm({ ...form, recurring: v })} />
            </div>

            {form.recurring && (
              <div className="space-y-2 p-3 rounded-md border border-border bg-background">
                <Select value={form.recurrence_pattern} onValueChange={v => setForm({ ...form, recurrence_pattern: v })}>
                  <SelectTrigger className="bg-background border-border"><SelectValue placeholder="Pattern" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {RECURRENCE_PATTERNS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
                {form.recurrence_pattern === 'Weekly' && (
                  <div className="flex gap-1 flex-wrap">
                    {WEEKDAYS.map(d => (
                      <Button key={d} size="sm" variant={form.recurrence_days.includes(d) ? 'default' : 'outline'}
                        className={`h-7 w-9 text-xs ${form.recurrence_days.includes(d) ? 'bg-primary text-primary-foreground' : 'border-border'}`}
                        onClick={() => {
                          const days = form.recurrence_days.includes(d) ? form.recurrence_days.filter(x => x !== d) : [...form.recurrence_days, d];
                          setForm({ ...form, recurrence_days: days });
                        }}
                      >{d}</Button>
                    ))}
                  </div>
                )}
                {form.recurrence_pattern === 'Monthly' && (
                  <div><Label className="text-xs text-muted-foreground">Day of month</Label><Input type="number" min={1} max={31} value={form.recurrence_day_of_month} onChange={e => setForm({ ...form, recurrence_day_of_month: e.target.value })} className="bg-background border-border" /></div>
                )}
                {form.recurrence_pattern === 'Custom' && (
                  <div><Label className="text-xs text-muted-foreground">Every N days</Label><Input type="number" min={1} value={form.recurrence_interval} onChange={e => setForm({ ...form, recurrence_interval: e.target.value })} className="bg-background border-border" /></div>
                )}
                <p className="text-xs text-muted-foreground">Recurring payments occur on their scheduled date regardless of day of week.</p>
              </div>
            )}

            <div><Label className="text-xs text-muted-foreground">Notes</Label><RichTextEditor value={form.notes} onChange={v => setForm({ ...form, notes: v })} placeholder="Notes..." collapsedHeight={80} /></div>

            <div>
              <Label className="text-xs text-muted-foreground">Linked Project</Label>
              <SearchableSelect
                value={form.linked_project || ''}
                onValueChange={v => setForm({ ...form, linked_project: v })}
                options={[{ value: '', label: '-- None --' }, ...projects.map(p => ({ value: p.id, label: `${p.project_number} - ${p.name}` }))]}
                placeholder="None"
                searchPlaceholder="Search projects..."
                data-testid="acct-event-form-project"
              />
            </div>

            {/* Reminder */}
            <div>
              <Label className="text-xs text-muted-foreground flex items-center gap-1"><Bell className="h-3 w-3" /> Reminder</Label>
              <Select value={form.reminder_days || '__none'} onValueChange={v => setForm({ ...form, reminder_days: v === '__none' ? '' : v })}>
                <SelectTrigger className="bg-background border-border"><SelectValue placeholder="No reminder" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="__none">No reminder</SelectItem>
                  {REMINDER_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* JE Draft Option */}
            {showJEDraftOption && (
              <Card className="border-amber-500/20 bg-amber-500/5 shadow-none">
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-amber-400" />
                      <div>
                        <p className="text-xs font-medium text-amber-400">Create Journal Entry Draft</p>
                        <p className="text-xs text-muted-foreground">Auto-create an expense JE when this event is saved</p>
                      </div>
                    </div>
                    <Switch checked={form.create_je_draft} onCheckedChange={v => setForm({ ...form, create_je_draft: v })} data-testid="acal-je-draft-toggle" />
                  </div>
                  {form.create_je_draft && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Amount ($)</Label>
                      <Input type="number" min={0} step="0.01" value={form.je_amount} onChange={e => setForm({ ...form, je_amount: e.target.value })} className="bg-background border-border" placeholder="0.00" data-testid="acal-je-amount" />
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <Button onClick={handleSave} className="w-full bg-primary text-primary-foreground mt-2" data-testid="acal-form-submit">
              {editEvent ? 'Update Event' : 'Create Event'}
            </Button>

            {editEvent && (
              <Button variant="destructive" className="w-full mt-1" onClick={handleDelete} data-testid="acal-delete-btn">
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete Event
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
